/**
 * Bulk Discount Management Component (Phase 2)
 * Admin interface for managing bulk discount rules
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Alert, Modal, Form,
  Spinner, ListGroup
} from 'react-bootstrap';
import { createDiscountRule, listDiscountRules, getBillingConfig } from '../../services/billingPhase2Service';
import schoolsService from '../../services/schoolsService';
import { Money, PlusCircle, Lightbulb, CheckCircle } from '@phosphor-icons/react';
import './BulkDiscountManagement.css';

export const BulkDiscountManagement = () => {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Discount rules are per-school (the backend requires school_id on every
  // call) — a super admin has no school of their own, so one must be picked.
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [unitPrice, setUnitPrice] = useState(400); // falls back to the platform default until the school's config loads

  // Form state
  const [formData, setFormData] = useState({
    minStudents: '',
    maxStudents: '',
    discountPercentage: '',
    description: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await schoolsService.listSchools();
        const list = Array.isArray(data) ? data : (data.schools || data.data || []);
        setSchools(list);
        if (list.length > 0) {
          setSelectedSchoolId(list[0].id);
        }
      } catch (err) {
        setError('Failed to load schools list');
      }
    })();
  }, []);

  const loadRules = useCallback(async () => {
    if (!selectedSchoolId) return;
    setLoading(true);
    setError(null);

    try {
      const [rulesData, config] = await Promise.all([
        listDiscountRules(selectedSchoolId),
        getBillingConfig(selectedSchoolId),
      ]);
      setRules(rulesData);
      setUnitPrice(config.unit_price || 400);
    } catch (err) {
      setError(err.error || 'Failed to load discount rules');
    } finally {
      setLoading(false);
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateRule = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedSchoolId) {
      setError('Select a school first');
      return;
    }
    if (!formData.minStudents || !formData.discountPercentage) {
      setError('Minimum students and discount percentage are required');
      return;
    }

    setLoading(true);

    try {
      const result = await createDiscountRule(
        selectedSchoolId,
        parseInt(formData.minStudents),
        parseFloat(formData.discountPercentage),
        formData.maxStudents ? parseInt(formData.maxStudents) : null,
        formData.description
      );

      setSuccess(`Discount rule created successfully: ${result.description}`);
      setFormData({ minStudents: '', maxStudents: '', discountPercentage: '', description: '' });
      setShowCreateModal(false);
      loadRules();
    } catch (err) {
      setError(err.error || 'Failed to create discount rule');
    } finally {
      setLoading(false);
    }
  };

  const getSavingsExamples = (discountPercentage) => {
    return [
      { students: 100, amount: (100 * unitPrice * discountPercentage / 100).toFixed(2) },
      { students: 500, amount: (500 * unitPrice * discountPercentage / 100).toFixed(2) },
      { students: 1000, amount: (1000 * unitPrice * discountPercentage / 100).toFixed(2) },
    ];
  };

  return (
    <Container fluid className="bulk-discount-management mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="section-title"><Money size={20} className="me-2" /> Bulk Discount Management</h2>
          <p className="text-muted">Create and manage volume-based discount rules for schools</p>
        </Col>
        <Col md="auto" className="d-flex align-items-center gap-2">
          <Form.Select
            value={selectedSchoolId}
            onChange={(e) => setSelectedSchoolId(e.target.value)}
            style={{ minWidth: '220px' }}
          >
            {schools.length === 0 && <option value="">No schools found</option>}
            {schools.map((school) => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </Form.Select>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            disabled={loading || !selectedSchoolId}
            size="lg"
          >
            <PlusCircle size={18} className="me-2" />
            Create Discount Rule
          </Button>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Active Rules</h6>
              <h3 className="mb-0">{rules.filter(r => r.is_active).length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Rules</h6>
              <h3 className="mb-0">{rules.length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Avg Discount Rate</h6>
              <h3 className="mb-0">
                {rules.length > 0
                  ? (rules.reduce((sum, r) => sum + r.discount_percentage, 0) / rules.length).toFixed(1)
                  : '0'}
                %
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Discount Rules Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Discount Rules</h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : rules.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Min Students</th>
                        <th>Max Students</th>
                        <th>Discount</th>
                        <th>Description</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((rule) => (
                        <tr key={rule.id}>
                          <td>
                            <strong>{rule.min_students.toLocaleString()}</strong>
                          </td>
                          <td>
                            {rule.max_students
                              ? rule.max_students.toLocaleString()
                              : 'Unlimited'}
                          </td>
                          <td>
                            <Badge bg="info" className="fs-6">
                              {rule.discount_percentage ? `${rule.discount_percentage}%` : `GHS ${rule.discount_amount}`}
                            </Badge>
                          </td>
                          <td>
                            <small>{rule.description}</small>
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={rule.is_active ? 'success' : 'secondary'}
                              pill
                            >
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="alert alert-info" role="alert">
                  <strong>No discount rules created yet.</strong> Click "Create Discount Rule" to add one.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Discount Examples */}
      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><Lightbulb size={18} className="me-2" /> Discount Impact Examples</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                See how bulk discounts affect total billing for schools with different student counts
                (based on this school's GHS {unitPrice}/student rate):
              </p>
              <Row>
                {rules.length > 0 ? (
                  rules.slice(0, 3).map((rule) => (
                    <Col md={4} key={rule.id} className="mb-3">
                      <Card className="bg-light">
                        <Card.Body>
                          <h6 className="mb-3 text-primary">{rule.description}</h6>
                          <table className="example-table w-100">
                            <tbody>
                              <tr>
                                <td>100 students</td>
                                <td className="text-end fw-bold">
                                  GHS {((100 * unitPrice * (1 - rule.discount_percentage / 100)).toFixed(2))}
                                </td>
                              </tr>
                              <tr>
                                <td>500 students</td>
                                <td className="text-end fw-bold">
                                  GHS {((500 * unitPrice * (1 - rule.discount_percentage / 100)).toFixed(2))}
                                </td>
                              </tr>
                              <tr>
                                <td>1000 students</td>
                                <td className="text-end fw-bold">
                                  GHS {((1000 * unitPrice * (1 - rule.discount_percentage / 100)).toFixed(2))}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <small className="text-success d-block mt-2">
                            <CheckCircle size={14} className="me-1" />
                            Saves {rule.discount_percentage}% on total bill
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                ) : (
                  <Col>
                    <div className="alert alert-info" role="alert">
                      Create discount rules to see impact examples
                    </div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Discount Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Students *</Form.Label>
                  <Form.Control
                    type="number"
                    name="minStudents"
                    value={formData.minStudents}
                    onChange={handleInputChange}
                    placeholder="e.g., 500"
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Discount applies when school has this many students or more
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Maximum Students (Optional)</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxStudents"
                    value={formData.maxStudents}
                    onChange={handleInputChange}
                    placeholder="Leave blank for unlimited"
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Leave blank if no upper limit
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Discount Percentage *</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  name="discountPercentage"
                  value={formData.discountPercentage}
                  onChange={handleInputChange}
                  placeholder="e.g., 5"
                  min="0"
                  max="100"
                  step="0.5"
                />
                <span className="input-group-text">%</span>
              </div>
              <Form.Text className="text-muted">
                Percentage off the subscription amount
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="e.g., 500+ students get 5% off"
              />
            </Form.Group>

            {formData.discountPercentage && (
              <Alert variant="info">
                <strong>Savings Example:</strong>
                <ListGroup variant="flush" className="mt-2">
                  {getSavingsExamples(parseFloat(formData.discountPercentage) || 0).map((example) => (
                    <ListGroup.Item key={example.students} className="px-0">
                      {example.students} students: GHS {example.amount} saved
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateRule}
            disabled={loading || !formData.minStudents || !formData.discountPercentage}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              'Create Rule'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BulkDiscountManagement;
